import type { ReactNode } from 'react';
import { ApiRefusal, ApiUnreachable } from '@gatecheckpro/api-client';
import { Notice } from '@gatecheckpro/design-system';

/**
 * Turns a thrown failure into something a person can act on.
 *
 * Three things must stay distinguishable, because the right next move differs
 * for each: the network did not reach us, the server refused deliberately, or
 * something broke. Collapsing them into "Something went wrong" is how a product
 * teaches its users that its error messages are not worth reading.
 */
const REMEDY: Readonly<Record<string, string>> = {
  AUTH_INVALID_CREDENTIALS: 'Check the email address and password and try again.',
  AUTH_RATE_LIMITED: 'Too many attempts. Wait a minute and try again.',
  EMAIL_ALREADY_REGISTERED: 'Sign in instead, or use a different address.',
  PHONE_ALREADY_REGISTERED: 'That number is already on another account.',
  PHONE_INVALID: 'Check the number. Nigerian numbers look like 08031234567.',

  // Checkout. Each says what the person can actually do next — "sold out" and
  // "we have three, you asked for five" call for different actions.
  TICKETS_UNAVAILABLE: 'Someone else took the last of these. Nothing was charged.',
  QUANTITY_UNAVAILABLE: 'Try a smaller number.',
  CHECKOUT_EXPIRED: 'Your reservation ran out and the tickets went back on sale.',
  EVENT_NOT_ON_SALE: 'The organizer has taken this event down.',
  TICKET_PRODUCT_NOT_ON_SALE: 'The organizer is no longer offering this ticket.',
  IDEMPOTENCY_REQUEST_IN_FLIGHT: 'Your last attempt is still going through. Give it a moment.',
  IDEMPOTENCY_KEY_REUSED: 'Start again from the ticket you want.',

  /*
   * Choosing a seat. Each of these is a different thing to do next, which is
   * why they are separate reasons rather than one "that did not work".
   *
   * None of them says what happened in the room in the abstract — they say
   * what this person can do about it. Somebody whose chairs were taken while
   * they typed their card details does not need the word "concurrency"; they
   * need to know nothing was charged and which chairs to pick instead.
   */
  SEAT_SELECTION_CONFIGURATION_STALE:
    'The organizer changed what is on sale while you were choosing. Reload the room and pick again — your seats may still be free.',
  SEAT_SELECTION_UNAVAILABLE:
    'Someone else took one of those seats first. Nothing was charged. Pick again from what is still free.',
  SEAT_HOLD_EXPIRED:
    'The time on your seats ran out and they went back on sale. Nothing was charged. Choose again.',
  SEAT_SELECTION_TABLE_SOLD_WHOLE: 'That seat is only sold with its whole table. Take the table.',
  SEAT_SELECTION_TABLE_SOLD_BY_SEAT:
    'That table is sold seat by seat. Choose the seats you want around it.',
  // Added by CC1 with the reason itself, so this guard is not left red by a
  // contract change. CC2 owns the wording and should reword it freely: the
  // situation is a second tab or a retried request, the buyer's seats are safe,
  // and the remedy is the order they already have rather than choosing again.
  SEAT_HOLD_ALREADY_IN_CHECKOUT:
    'You have already started checking out with these seats. Nothing was charged twice — open the order you started.',
  SEATING_ACQUISITION_SPANS_ALLOCATIONS:
    'Those seats are not sold together. Choose seats of one kind, or buy them in separate orders.',
};

export function describeFailure(error: unknown): {
  tone: 'critical' | 'caution';
  title: string;
  message: string;
  detail: string | null;
} {
  if (error instanceof ApiUnreachable) {
    return {
      tone: 'caution',
      title: 'Could not reach GateCheckPro',
      message: 'Nothing was changed. Check the connection and try again.',
      detail: null,
    };
  }
  if (error instanceof ApiRefusal) {
    return {
      tone: error.status >= 500 ? 'critical' : 'caution',
      title: error.status >= 500 ? 'GateCheckPro could not complete that' : 'That was refused',
      message: error.message,
      /*
       * The reason first, then the code.
       *
       * A typed refusal arrives as a broad HTTP-shaped code with the specific
       * reason beside it — `RESOURCE_CONFLICT` with `SEAT_HOLD_EXPIRED`, say.
       * Keying only on the outer code would show a generic reference number for
       * every one of them while a perfectly good remedy sat unused.
       */
      // The request id is what support correlates against the audit trail.
      detail:
        REMEDY[error.reasonCode ?? ''] ??
        REMEDY[error.code] ??
        (error.requestId === '' ? null : `Reference ${error.requestId}`),
    };
  }
  return {
    tone: 'critical',
    title: 'Something went wrong',
    message: 'The workspace hit an unexpected problem. Reload and try again.',
    detail: null,
  };
}

export function FailureNotice({ error }: { readonly error: unknown }): ReactNode {
  if (error === null || error === undefined) return null;
  const described = describeFailure(error);
  return (
    <Notice tone={described.tone} title={described.title}>
      {described.message}
      {described.detail !== null && (
        <div className="gc-muted" style={{ marginTop: '0.25rem' }}>
          {described.detail}
        </div>
      )}
    </Notice>
  );
}
